<template>
  <div class="d-flex flex-column full-height">
    <OnboardingTour
      name="backpack"
      :steps="onboarding.steps"
      :options="onboarding.options"
      @finish="onOnboardingFinished(false)"
    />

    <div class="d-flex pa-3" data-onboarding="backpack-header">
      <div class="grey400 px-2 py-1 mr-1 d-flex flex-column justify-center">
        <h4 class="font-weight-semibold lh-1-2 text-caption text-no-wrap">
          <span v-if="selectedItems.length">
            <span class="blue300--text">{{ selectedItems.length | toLocaleInt }}</span>
            <span> / </span>
            <span>{{ items.length | toLocaleInt }}</span>
          </span>
          <span v-else>{{ items.length | toLocaleInt }} items</span>
        </h4>
        <h5 class="font-weight-semibold lh-1-2 text-caption">
          <span v-if="selectedItems.length" class="blue300--text">${{ selectedValue | toScrap }}</span>
          <span v-else class="text--secondary">${{ inventoryValue | toScrap }}</span>
        </h5>
      </div>

      <!-- search -->
      <v-text-field
        :value="inputs.search"
        class="mr-1 rounded-0 search-input"
        background-color="grey400"
        :placeholder="$t('common.search')"
        hide-details solo flat dense
        @input="onSearchInput"
      />

      <!-- filter -->
      <v-menu
        bottom
        right
        offset-y
        :close-on-content-click="false"
        nudge-bottom="4"
        content-class="rounded-0"
      >
        <template #activator="{ on }">
          <v-btn
            :color="inputs.flags.length ? 'green800 green200--text' : 'grey400 grey100--text'"
            class="mr-1 rounded-0 unwidth unheight"
            :ripple="false" tile small
            data-onboarding="backpack-filter"
            v-on="on"
          >
            <fai
              class="text-body-2"
              :icon="['fas','filter']"
            />
          </v-btn>
        </template>
        <div class="grey400 px-3 py-2">
          <v-checkbox
            v-for="(flag, key) in ITEM_FLAGS" :key="key"
            v-model="inputs.flags" dense hide-details
            :ripple="false"
            color="green500" class="my-0"
            :value="flag" :label="$t(`sidebar.backpack.${key}`)"
          />
        </div>
      </v-menu>

      <!-- refresh -->
      <v-btn
        color="blue700" class="rounded-0 unwidth unheight"
        :ripple="false" tile small :disabled="loading.inventory"
        @click="onGetInventory"
      >
        <fai
          class="blue300--text text-body-2"
          :spin="loading.inventory"
          :icon="['fas','sync']"
        />
      </v-btn>
    </div>

    <div v-if="pendingTrades.length" class="px-3 pb-3">
      <v-btn
        color="green800 green400--text"
        class="text-body-2 font-weight-bold text-none"
        tile depressed :ripple="false" block
        @click="$modal.open('account', { maxWidth: 1000 }, {tab: 'trades'})"
      >
        <fai :icon="['fad', 'sync']" spin class="mr-2" />
        You have active trades offers
      </v-btn>
    </div>

    <div class="user-items flex-grow-1 py-3 pl-3 grey800" data-onboarding="backpack-inv">
      <Inventory
        :items="filteredItems"
        :min-item-size="100"
        :loading="loading.inventory"
        :item-component="itemComponent"
        @onItemClick="onSelectItem"
      >
        <template #no-items>
          <div v-if="onboarding.active" class="pt-8 full-height d-flex align-center flex-column justify-center">
            <h4 class="text-body-1 text--disabled">
              Please finish the guide first
            </h4>
            <v-btn
              small text tile :ripple="false"
              @click="onOnboardingFinished(true)"
            >
              Or click here to skip it
            </v-btn>
          </div>
          <div v-else-if="items.length && isFiltering" class="pt-8 full-height d-flex flex-column align-center ">
            <fai :icon="['fad', 'exclamation-triangle']" class="text-h4 text--disabled mb-3" />
            <h4 v-t="'inventory.no_items_filtered'" class="text-body-2 text--disabled" />
          </div>
          <BackpackEmpty
            v-else
            class="full-height"
            @deposit="onDeposit"
            @buy="onShop"
          />
        </template>
      </Inventory>
    </div>

    <div class="flex-grow-0 grey700 pa-3" data-onboarding="backpack-footer">
      <v-row dense>
        <v-col v-if="!selectedItems.length" cols="12">
          <v-row dense>
            <v-col cols="6">
              <v-btn
                color="blue700 blue200--text"
                tile depressed :ripple="false" block
                large
                data-onboarding="backpack-deposit"
                @click="onDeposit"
              >
                <fai :icon="['fab', 'steam']" class="mr-2" />
                <span class="font-weight-semibold" v-text="'Deposit'" />
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                color="blue800 blue200--text"
                tile depressed :ripple="false" block
                large
                data-onboarding="backpack-buy"
                @click="onShop"
              >
                <fai :icon="['fad', 'box-full']" class="mr-2" />
                <span class="font-weight-semibold" v-text="'Item shop'" />
              </v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col v-else cols="12">
          <v-row dense>
            <v-col cols="6">
              <v-tooltip
                top content-class="rounded-0"
                color="primary500"
                :disabled="!sellDisabled || sellDisabled === true"
              >
                <template #activator="{on}">
                  <div v-on="on">
                    <v-btn
                      color="green700 green300--text"
                      tile depressed :ripple="false" block
                      large
                      :disabled="!!selectedItems.length && !!sellDisabled"
                      @click="onSell"
                    >
                      <fai fixed-width :icon="['fad', 'coins']" class="mr-1" />
                      <span class="font-weight-semibold" v-text="'Sell'" />
                      <span v-if="bonus" class="ml-1">(+{{ bonus * 100 | toLocale }}%)</span>
                    </v-btn>
                  </div>
                </template>
                <span>
                  <fai :icon="['fas', 'exclamation-triangle']" class="mr-2" />
                  <span class="font-weight-semibold" v-text="sellDisabled" />
                </span>
              </v-tooltip>
            </v-col>
            <v-col cols="6">
              <v-tooltip
                top right
                content-class="rounded-0"
                color="primary500"
                :disabled="!swapDisabled || swapDisabled === true"
              >
                <template #activator="{on}">
                  <div v-on="on">
                    <v-btn
                      color="blue700 blue200--text"
                      tile depressed :ripple="false" block
                      large
                      :disabled="!!selectedItems.length && !!swapDisabled"
                      data-onboarding="backpack-swap"
                      @click="onSwap"
                    >
                      <fai fixed-width :icon="['fad', 'exchange']" class="mr-1" />
                      Swap (free)
                    </v-btn>
                  </div>
                </template>
                <span>
                  <fai :icon="['fas', 'exclamation-triangle']" class="mr-2" />
                  <span class="font-weight-semibold" v-text="swapDisabled" />
                </span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="6">
          <v-btn
            color="grey400 text--secondary"
            tile depressed :ripple="false" block
            small @click="onSelectAll"
          >
            <span class="font-weight-semibold" v-text="'Select all'" />
          </v-btn>
        </v-col>
        <v-col cols="6">
          <v-tooltip
            top right
            content-class="rounded-0"
            color="primary500"
            :disabled="!withdrawDisabled || withdrawDisabled === true"
          >
            <template #activator="{on}">
              <div v-on="on">
                <v-btn
                  color="grey400 text--secondary"
                  tile depressed :ripple="false" block
                  small
                  :disabled="!!selectedItems.length && !!withdrawDisabled"
                  :loading="loading.withdraw"
                  data-onboarding="backpack-withdraw"
                  @click="onWithdraw"
                >
                  <span class="font-weight-semibold" v-text="$t('cta.withdraw')" />
                </v-btn>
              </div>
            </template>
            <span>
              <fai :icon="['fas', 'exclamation-triangle']" class="mr-2" />
              <span class="font-weight-semibold" v-text="withdrawDisabled" />
            </span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import sortBy from 'lodash.sortby'
import { debounce } from '@/utils'

import Inventory from '@/components/Inventory'
import BackpackItem from '@/components/BackpackItem'
import BackpackEmpty from '@/components/BackpackEmpty'
import Item, { ITEM_FLAGS } from '@/store/models/user/Item'
import { itemPickupSound } from '@/modules/item-sounds'

import OnboardingMixin from '@/mixins/onboarding'
import OnboardingTour from '@/components/OnboardingTour'

const FAKE_ITEM = {
  name: 'Digital Camo MP5',
  price: 1337,
  color: '#f15840',
  image: '6TMcQ7eX6E0EZl2byXi7vaVKyDk_zQLX05x6eLCFM9neAckxGDf7qU2e2gu64OnAeQ7835Fe5GPEfCk4nReh8DEiv5dYPK04rLY_Qfi-wUTqOxk',
  category: 'Weapon',
  type: 'Mp5',
  canWithdraw: true,
  canSell: true,
  canBet: true,
  canTip: true,
  inUse: false,
  inStock: false,
  selected: false,
}

export default {
  components: {
    Inventory,
    BackpackEmpty,
    OnboardingTour,
  },
  mixins: [OnboardingMixin],
  data() {
    return {
      inputs: {
        search: '',
        flags: [],
      },
      loading: {
        inventory: false,
        withdraw: false,
      },
      onboarding: {
        active: false,
        items: [],
        options: {
          enabledButtons: {
            buttonSkip: false,
          },
          enabledNavigationKeys: {
            escape: false,
          },
        },
        steps: [
          {
            target: '[data-onboarding="backpack-header"]',
            header: { title: 'Backpack Guide' },
            content: 'Please go through this small tour to better understand how the backpack system works.',
          },
          {
            target: '[data-onboarding="backpack-deposit"]',
            header: { title: 'Deposit' },
            params: { highlight: true },
            content: 'Deposited items from Steam go into your backpack.',
          },
          {
            target: '[data-onboarding="backpack-buy"]',
            header: { title: 'Item shop' },
            params: { highlight: true },
            content: 'Buy items from the shop to play with or withdraw them.',
          },
          {
            target: '[data-onboarding="backpack-inv"]  .item-row',
            header: { title: 'Items have attributes' },
            params: { highlight: true, enableScrolling: false },
            content: 'Not all items are the same, here we have 3 (fake) MP5s. What\'s different?',
            before: async type => {
              this.onAddOnboardingItems()
              await this.$nextTick()
            },
          },
          {
            target: '[data-onboarding="backpack-inv"] .item .item-flag.fa-coins',
            header: { title: 'Sellable' },
            params: { highlight: true, enableScrolling: false },
            content: 'This item can be sold for scrap for use in games or the shop.',
          },
          {
            target: '[data-onboarding="backpack-inv"] .item',
            header: { title: 'Deposit requirement' },
            params: { enableScrolling: false },
            content: 'Items you <b>deposit</b> are not withdrawable until you bet them. This is to prevent trading/abusers.',
          },
          {
            target: '[data-onboarding="backpack-inv"] .item .item-flag.fa-arrow-alt-square-up.green400--text',
            header: { title: 'Withdrawable' },
            params: { highlight: true, enableScrolling: false },
            content: 'This item can be withdrawn and is in stock.',
          },
          {
            target: '[data-onboarding="backpack-inv"] .item .item-flag.fa-arrow-alt-square-up.primary400--text',
            header: { title: 'Withdrawable, not in stock' },
            params: { highlight: true, enableScrolling: false },
            content: 'This item could be withdrawn, if it was in stock. What now?',
          },
          {
            target: '[data-onboarding="backpack-swap"]',
            header: { title: 'Swap, don\'t sell' },
            params: { highlight: true, enableScrolling: false },
            content: 'Select it and simply swap it for another item. Selling is only meant for playing games.',
            before: async type => {
              this.onboarding.items[2].selected = true
              this.onboarding.items[1].selected = false
              await this.$nextTick()
            },
          },
          {
            target: '[data-onboarding="backpack-withdraw"]',
            header: { title: 'Withdraw' },
            params: { highlight: true, enableScrolling: false },
            content: 'Items that are in stock can be withdrawn at any time.',
            before: async type => {
              this.onboarding.items[2].selected = false
              this.onboarding.items[1].selected = true
              await this.$nextTick()
            },
          },
          {
            target: '[data-onboarding="backpack-filter"]',
            header: { title: 'Item filter' },
            params: { highlight: true },
            content: 'You can filter your backpack by these attributes.',
          },
        ],
      },
    }
  },
  computed: {
    ...mapGetters({
      inventory: 'user/inventory',
      conga: 'conga/conga',
      itemdb: 'items/items',
      user: 'user/session',
      settings: 'settings/settings',
      pendingTrades: 'user/pendingTrades',
    }),
    ITEM_FLAGS() {
      return ITEM_FLAGS
    },
    isFiltering() {
      if (this.inputs.search) return true
      if (this.inputs.flags.length) return true
      return false
    },
    bonus() {
      const depositBonus = this.conga.deposits?.market?.bonus ?? 0
      const affBonus = this.user.hasRefCode ? this.conga.affiliates?.bonus?.market ?? 0 : 0

      return depositBonus + affBonus
    },
    itemComponent() {
      return BackpackItem
    },
    items() {
      if (this.onboarding.active) return this.onboarding.items

      return sortBy(this.inventory, 'price').reverse()
    },
    selectedItems() {
      return this.items.filter(i => i.selected)
    },
    selectedValue() {
      return this.selectedItems.reduce((acc, v) => acc + v.price, 0)
    },
    inventoryValue() {
      return this.items.reduce((acc, v) => acc + v.price, 0)
    },
    filteredItems() {
      let items = [...this.items]

      const { search, flags } = this.inputs

      if (search) {
        items = items.filter(i => (i?.name ?? '').toLowerCase().includes(search.toLowerCase()))
      }

      if (flags.length) {
        items = items.filter(i => flags.every(flag => !!(i.flags & flag)))
      }

      return items
    },
    //
    someSelectedNoPrice() {
      return this.selectedItems.some(i => !i.price)
    },
    allSelectedSameFlags() {
      if (!this.selectedItems.length) return false

      const [firstItem] = this.selectedItems
      if (this.selectedItems.some(i => i.flags !== firstItem.flags)) return false

      return true
    },
    withdrawDisabled() {
      if (!this.selectedItems.length) return true
      if (this.someSelectedNoPrice) return this.$t('inventory.items.some.no_price')
      if (this.selectedItems.some(i => !i.isWithdrawable)) return this.$t('inventory.items.some.not_withdrawable')
      return false
    },
    sellDisabled() {
      if (!this.selectedItems.length) return true
      if (this.someSelectedNoPrice) return this.$t('inventory.items.some.no_price')
      if (this.selectedItems.some(i => !i.isSellable)) return this.$t('inventory.items.some.not_sellable')
      return false
    },
    swapDisabled() {
      if (!this.selectedItems.length) return true
      if (this.someSelectedNoPrice) return this.$t('inventory.items.some.no_price')
      if (!this.allSelectedSameFlags) return this.$t('inventory.items.some.not_same')
      if (this.selectedItems.some(i => !i.canWithdraw)) return 'Only withdrawable items can be swapped'
      return false
    },
  },
  watch: {
    user(val, old) {
      if (!old && val) {
        this.onGetInventory()
      }
    },
  },
  created() {
    if (this.user) {
      this.onGetInventory()
    }
  },
  mounted() {
    // start onboarding tour
    if (this.user && !this.onboardingDisabled && !this.onboardingHasSeen('backpack')) {
      this.$tours.backpack.start()
      this.onboarding.active = true
    }
  },
  methods: {
    ...mapActions({
      getInventory: 'user/getInventory',
    }),
    ...mapMutations({
      selectItem: 'user/selectItem',
      selectItems: 'user/selectItems',
    }),
    onSelectAll() {
      if (Object.values(this.loading).some(Boolean)) return

      const selectable = this.filteredItems.filter(i => i.canSelect())
      if (!selectable.length) return

      const allSelected = selectable.every(i => i.selected)

      this.selectItems([selectable.map(i => i.id), !allSelected])

      const volume = this.settings.sounds.volumes.items
      itemPickupSound('bone', volume)
      itemPickupSound('gun', volume)
      itemPickupSound('platic', volume)
    },
    onSelectItem(item) {
      if (Object.values(this.loading).some(Boolean)) return
      if (!item.selected && !item.canSelect()) return

      this.selectItem([item.id, !item.selected])

      const volume = this.settings.sounds.volumes.items
      itemPickupSound(item.type, volume)
    },
    async onGetInventory() {
      if (Object.values(this.loading).some(Boolean)) return

      if (!this.user) {
        this.$modal.open('auth', { maxWidth: 320, persistent: true })
        return
      }

      try {
        this.loading.inventory = true
        await this.getInventory()
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.inventory = false
      }
    },
    onSearchInput: debounce(function onChange(search) {
      this.inputs.search = search.trim()
    }, 200),
    onSell() {
      if (this.onboarding.active) return

      if (!this.user) {
        this.$modal.open('auth', { maxWidth: 320, persistent: true })
        return
      }

      if (this.sellDisabled) return
      this.$modal.open('sell-items', { maxWidth: 400 }, { items: this.selectedItems })
    },
    onWithdraw() {
      if (this.onboarding.active) return

      if (!this.user) {
        this.$modal.open('auth', { maxWidth: 320, persistent: true })
        return
      }

      if (this.withdrawDisabled) return
      this.$modal.open('withdraw-items', { maxWidth: 400 }, { items: this.selectedItems })
    },
    onSwap() {
      if (this.onboarding.active) return

      if (!this.user) {
        this.$modal.open('auth', { maxWidth: 320, persistent: true })
        return
      }

      if (this.swapDisabled) return

      if (this.selectedItems[0].canWithdraw) {
        // show intent popup
        this.$modal.open(
          'backpack-intent',
          { maxWidth: 600, noShadow: true },
          { action: 'swap', items: this.selectedItems },
        )
        return
      }

      this.$modal.open('swap-items', { maxWidth: 800, noShadow: true }, { items: this.selectedItems })
    },
    onDeposit() {
      if (this.onboarding.active) return

      if (!this.user) {
        this.$modal.open('auth', { maxWidth: 320, persistent: true })
        return
      }

      this.$modal.open('backpack-deposit', { maxWidth: 900, noShadow: true })
    },
    onShop() {
      if (this.onboarding.active) return

      if (!this.user) {
        this.$modal.open('auth', { maxWidth: 320, persistent: true })
        return
      }

      this.$modal.open('backpack-intent', { maxWidth: 600, noShadow: true }, { action: 'shop' })
    },
    //
    onAddOnboardingItems() {
      this.onboarding.items = [
        new Item({ id: 1, ...FAKE_ITEM, canWithdraw: false }),
        new Item({ id: 2, ...FAKE_ITEM, inStock: true }),
        new Item({ id: 3, ...FAKE_ITEM }),
      ]
    },
    onOnboardingFinished(skipped = false) {
      this.onboarding.active = false
      this.onboarding.items = []
      this.onGetInventory()

      if (skipped) {
        this.$tours.backpack.stop()
      } else {
        this.$toast.info('Thanks for doing the Backpack tour :)')
      }

      this.onboardingSetSeen('backpack')
    },
  },
}
</script>

<style lang="scss" scoped>
.user-items {
  overflow: hidden;
  position: relative;
  min-height: 200px;
}

.empty-pic {
  filter: grayscale(0.8);
}

.v-tour__target--highlighted {
  box-shadow:
    0 0 15px blue(100),
    0 0 25px blue(400),
    0 0 40px blue(500),
    0 0 60px blue(600),
    0 0 0 99999px rgba(blue(900), 0.7);

  ::v-deep &.item-flag {
    box-shadow: none;
    filter:
      drop-shadow(0 0 2px rgba(black, 0.4))
      drop-shadow(0 0 5px rgba(black, 0.7))
      drop-shadow(0 0 12px rgba(black, 0.8));
    transform: scale(1.5);
  }
}
</style>
